import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot} from '@angular/router';

import * as moment from 'moment';

import {map} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {switchMap} from 'rxjs/internal/operators/switchMap';

import {
	HttpStatusCodesEnum,
	DocumentTypesEnum,
	EnrollChannelsEnum,
	UserRelationTypesEnum,
	FinancialQuestionnaireTypesEnum,
	AuthApiService,
	IndividualsService,
	UserModel,
	IndividualExtendedModel,
	IndividualDocumentModel,
	IndividualFinancialQuestionnaireModel,
	TenantsEnum,
	CompanyEmployeesService, ServerResponse, CompanyEmployeeModel, ReportsService,
} from '@spa/api';

import {Helper} from '../classes';
import {AuthenticationPaths, EnrollPaths} from '../paths';
import {LanguageService} from '../language.service';
import {BadRequestErrorKey, ErrorMessageIdSelector} from '../constants';

import {CacheCollectionService} from './cache-collection.service';
import {environment} from 'src/environments/environment';

@Injectable()
export class ClientRedirectGuardService implements CanActivateChild {
	private currentLanguage: string;

	public forceRedirect: boolean;

	constructor(
		private window: Window,
		private router: Router,
		private reportsService: ReportsService,
		private authApiService: AuthApiService,
		private languageService: LanguageService,
		private individualsService: IndividualsService,
		private employeesService: CompanyEmployeesService,
		private cacheCollectionService: CacheCollectionService,
	) {
		this.forceRedirect = false;
		this.currentLanguage = '';
	}

	private navigateToErrorPage(status: number, messages: string[]): void {
		const timestamp = moment().unix();

		const collectionItemData = {statuses: [status], messages};

		if (this.cacheCollectionService) {
			this.cacheCollectionService.addItem(timestamp.toString(), collectionItemData);
		}

		this.router.navigate([`${this.currentLanguage}/error`], {
			queryParams: {
				[ErrorMessageIdSelector]: timestamp
			}
		}).finally();
	}

	private getEmployeeUrl(
		authUser: UserModel,
		client: CompanyEmployeeModel,
		state: RouterStateSnapshot
	): Observable<string> {
		if (this.forceRedirect) {
			this.forceRedirect = false;

			return of(state.url);
		}

		const authRoot = `${this.currentLanguage}/${AuthenticationPaths.root}`;

		if (client && !authUser.phoneNumberVerified) {
			return of(`${authRoot}/${AuthenticationPaths.children.confirmPhone.path}`);
		} else if (!client) {
			return this.authApiService.loginRedirect(true).pipe(map(x => ''));
		}

		return of(state.url);
	}

	private getIndividualUrl(
		authUser: UserModel,
		client: IndividualExtendedModel | null,
		state: RouterStateSnapshot
	): Observable<string> {
		const enrollPaths = this.getEnrollPaths();
		const authRoot = `${this.currentLanguage}/${AuthenticationPaths.root}`;

		if (client && client.enrollChannel === EnrollChannelsEnum.Offline) {
			if (this.forceRedirect) {
				this.forceRedirect = false;

				return of(state.url);
			}

			if (client && enrollPaths.includes(state.url)) {
				const redirectPath = Helper.getPortalHomepage(this.currentLanguage, true);

				return of(redirectPath);
			}

			return this.getEnrollUrl(authUser, client as IndividualExtendedModel, state);
		} else if (
			client &&
			!Helper.getEnrollStatuses().includes(client.status) &&
			!client.phoneNumberConfirmed
		) {
			return of(`${authRoot}/${AuthenticationPaths.children.confirmPhone.path}`);
		} else if (
			client &&
			!Helper.getEnrollStatuses().includes(client.status) &&
			enrollPaths.includes(state.url)
		) {
			const redirectPath = Helper.getPortalHomepage(this.currentLanguage, true);

			return of(redirectPath);
		} else {
			return this.getEnrollUrl(authUser, client as IndividualExtendedModel | null, state);
		}
	}

	private getEnrollPaths(): string[] {
		const enrollRoot = `${this.currentLanguage}/${EnrollPaths.root}`;

		return [
			`${enrollRoot}/${EnrollPaths.children.phoneNumber.path}`,
			`${enrollRoot}/${EnrollPaths.children.personalDetails.path}`,
			`${enrollRoot}/${EnrollPaths.children.taxDetails.path}`,
			`${enrollRoot}/${EnrollPaths.children.addresses.path}`,
			`${enrollRoot}/${EnrollPaths.children.financialExperience.path}`,
			`${enrollRoot}/${EnrollPaths.children.financialKnowledge.path}`,
			`${enrollRoot}/${EnrollPaths.children.purposeOfTrading.path}`,
			`${enrollRoot}/${EnrollPaths.children.employmentStatus.path}`,
			`${enrollRoot}/${EnrollPaths.children.sourceOfFunds.path}`,
			`${enrollRoot}/${EnrollPaths.children.estimatedWorth.path}`,
			`${enrollRoot}/${EnrollPaths.children.proofOfIdentity.path}`,
			`${enrollRoot}/${EnrollPaths.children.proofOfAddress.path}`,
			`${enrollRoot}/${EnrollPaths.children.termsAndConditions.path}`,
		];
	}

	private getEnrollUrl(
		authUser: UserModel,
		individual: IndividualExtendedModel | null,
		state: RouterStateSnapshot
	): Observable<string> {
		if (individual && !Helper.getEnrollStatuses().includes(individual.individualStatus)) {
			return of(state.url);
		}

		if (!this.forceRedirect) {
			return this.redirectSteps(authUser, individual);
		}

		this.forceRedirect = false;

		return of(state.url);
	}

	private redirectSteps(authUser: UserModel, individual: IndividualExtendedModel | null): Observable<string> {
		const phoneVerified = individual?.phoneNumber && individual?.phoneNumberConfirmed;
		const isAlaric = environment.tenant === TenantsEnum.Alaric;

		let identityDocuments = [];
		let proofOfAddressDocument = [];

		if (individual && individual.documents.length > 0) {
			identityDocuments = individual.documents.filter((document: IndividualDocumentModel) => {
				return document.documentType !== DocumentTypesEnum.Selfie;
			});

			proofOfAddressDocument = individual.documents.filter((document: IndividualDocumentModel) => {
				return document.documentType === DocumentTypesEnum.ProofOfAddress;
			});
		}

		const enrollRoot = `${this.currentLanguage}/${EnrollPaths.root}`;

		if (!individual?.phoneNumber || !phoneVerified) {
			return of(`${enrollRoot}/${EnrollPaths.children.phoneNumber.path}`);
		}

		if (individual && !individual.birthPlace) {
			return of(`${enrollRoot}/${EnrollPaths.children.personalDetails.path}`);
		}

		if (individual && individual.taxDetails.length === 0) {
			return of(`${enrollRoot}/${EnrollPaths.children.taxDetails.path}`);
		}

		if (individual && (individual.addresses.length > 0 && !individual.addresses[0].addressLine)) {
			return of(`${enrollRoot}/${EnrollPaths.children.addresses.path}`);
		}

		if (isAlaric) {
			if (individual && !this.checkForFinancialExperienceAnswer(individual)) {
				return of(`${enrollRoot}/${EnrollPaths.children.financialExperience.path}`);
			}

			if (individual && individual.purposeOfTradingId === null) {
				return of(`${enrollRoot}/${EnrollPaths.children.purposeOfTrading.path}`);
			}

			if (individual && individual.sourceOfIncomes.length === 0) {
				return of(`${enrollRoot}/${EnrollPaths.children.employmentStatus.path}`);
			}

			if (individual && individual.sourceOfFunds.length === 0) {
				return of(`${enrollRoot}/${EnrollPaths.children.sourceOfFunds.path}`);
			}

			if (individual && individual.annualNetIncomeId === null || individual.estimatedNetWorthId === null) {
				return of(`${enrollRoot}/${EnrollPaths.children.estimatedWorth.path}`);
			}
		}

		if (individual && (individual.identityDetails.length === 0 || identityDocuments.length === 0)) {
			return of(`${enrollRoot}/${EnrollPaths.children.proofOfIdentity.path}`);
		}

		if (isAlaric) {
			if (proofOfAddressDocument.length === 0) {
				return of(`${enrollRoot}/${EnrollPaths.children.proofOfAddress.path}`);
			}

			if (isAlaric && individual && !individual.legalDeclarations) {
				return of(`${enrollRoot}/${EnrollPaths.children.termsAndConditions.path}`);
			}
		}

		const home = Helper.getPortalHomepage(this.currentLanguage, individual != null);

		return of(home);
	}

	private checkForFinancialExperienceAnswer(individual: IndividualExtendedModel): boolean {
		return this.checkFinancialQuestionExistence(
			individual,
			FinancialQuestionnaireTypesEnum.InvestmentExperienceWithCertificatesOrStructuredProducts
		);
	}

	private checkFinancialQuestionExistence(individual: IndividualExtendedModel, type: FinancialQuestionnaireTypesEnum): boolean {
		if (individual && (individual.individualFinancialQuestionnaires.length > 0)) {
			const filteredQuestionnaires = individual.individualFinancialQuestionnaires
				.find((question: IndividualFinancialQuestionnaireModel) => {
						return question.financialQuestionnaireType === type;
					}
				);

			return Boolean(filteredQuestionnaires);
		}

		return false;
	}

	private checkForFinancialKnowledgeAnswers(individual: IndividualExtendedModel): boolean {
		const knowledgeInProductsCheck = this.checkFinancialQuestionExistence(
			individual,
			FinancialQuestionnaireTypesEnum.ProfessionalKnowledgeInCertificatesOrStructuredProducts
		);

		const knowledgeInCCPCheck = this.checkFinancialQuestionExistence(
			individual,
			FinancialQuestionnaireTypesEnum.ProfessionalKnowledgeInCertificatesOrStructuredCapitalProtectionProductMaturity
		);

		const indexAtMaturityCheck = this.checkFinancialQuestionExistence(
			individual,
			FinancialQuestionnaireTypesEnum.FinancialIndexAtMaturityValue
		);

		return (knowledgeInProductsCheck && knowledgeInCCPCheck && indexAtMaturityCheck);
	}

	private getClientRequest(authUser: UserModel): ServerResponse<IndividualExtendedModel | CompanyEmployeeModel | null> {
		switch (authUser.userRelationType) {
			case UserRelationTypesEnum.Individual:

				return this.individualsService.getSelfExtended()
					.pipe(map(response => {
						if (response.status !== HttpStatusCodesEnum.Ok || !response.data) {
							const errorMessage = this.languageService.getInstantTranslationByKey(BadRequestErrorKey);

							this.navigateToErrorPage(HttpStatusCodesEnum.BadRequest, [errorMessage]);

							return Helper.errorsHelper().createResponseForInvalidData();
						}

						const individual = response.data as (IndividualExtendedModel | null);

						return Helper.createSuccessResponse<IndividualExtendedModel>(individual);
					}));
			case UserRelationTypesEnum.CompanyEmployee:
				return this.employeesService.getSelfCompanyEmployee(true)
					.pipe(map(employeeResponse => {
						if (employeeResponse.status !== HttpStatusCodesEnum.Ok || !employeeResponse.data) {
							const errorMessage = this.languageService.getInstantTranslationByKey(BadRequestErrorKey);

							this.navigateToErrorPage(HttpStatusCodesEnum.BadRequest, [errorMessage]);

							return Helper.errorsHelper().createResponseForInvalidData();
						}

						const employee = employeeResponse.data as CompanyEmployeeModel;

						return Helper.createSuccessResponse<CompanyEmployeeModel>(employee);
					}));
			default:
				const errorResponse = Helper.errorsHelper().createResponseForInvalidData();

				return of(errorResponse);
		}
	}

	canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
		const authUser = this.authApiService.getAuthenticatedUser();
		this.currentLanguage = state.url.slice(0, 3);

		/**
		 * We need to return true if user is not authenticated
		 * due to redirect of AuthGuardService to the login page
		 */
		if (!authUser || (!authUser.id)) {
			return of(true);
		}

		const userRelationTypeCheck = ([
			UserRelationTypesEnum.Individual,
			UserRelationTypesEnum.CompanyEmployee,
		].indexOf(authUser.userRelationType) >= 0);

		if (!userRelationTypeCheck) {
			return this.authApiService.loginRedirect(true).pipe(map(x => false));
		}

		return this.getClientRequest(authUser).pipe(
			switchMap(response => {
				if (childRoute.queryParams.forceRedirect || (childRoute.parent && childRoute.parent.data.hasOwnProperty('forceRedirect'))) {
					setTimeout(() => {
						const pathName = this.window.location.pathname;
						this.window.history.pushState({path: pathName}, '', pathName);
					});

					return of(true);
				}

				const urlFn = authUser.userRelationType === UserRelationTypesEnum.Individual
					? this.getIndividualUrl(authUser, response.data as IndividualExtendedModel, state)
					: this.getEmployeeUrl(authUser, response.data as CompanyEmployeeModel, state);

				return urlFn.pipe(map(url => {
						if (state.url !== url) {
							this.router.navigate([url]).finally();
						}

						return true;
					})
				);
			})
		);
	}
}
